import { useState } from "react";
import { Loader2, Download, Mail, MessageCircle, Wand2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { unlockRebuild } from "@/lib/api";
import { toast } from "sonner";

const GOALS = [
  { key: "", label: "Let AI decide" },
  { key: "customers", label: "Get more customers" },
  { key: "modernize", label: "Modernize my website" },
  { key: "ai", label: "Add AI & automation" },
];

/**
 * Reusable contact-capture gate. Collects ONE delivery method (email or WhatsApp)
 * plus optional name/goal, unlocks the concept, and hands the token back to the parent.
 * onUnlocked({ token, method }) — parent opens the PDF / reveals the full report.
 */
export default function RebuildGateDialog({ open, onOpenChange, conceptId, domain, onUnlocked }) {
  const [method, setMethod] = useState("email");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [consent, setConsent] = useState(false);
  const [goal, setGoal] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (method === "email") {
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) { toast.error("Please enter a valid email address"); return; }
    } else {
      if (whatsapp.replace(/[^\d]/g, "").length < 8) { toast.error("Enter a valid WhatsApp number with country code"); return; }
      if (!consent) { toast.error("Please tick the WhatsApp consent box to continue"); return; }
    }
    setBusy(true);
    try {
      const payload = method === "email"
        ? { method, email: email.trim(), name: name.trim(), goal }
        : { method, whatsapp: whatsapp.trim(), name: name.trim(), consent, goal };
      const res = await unlockRebuild(conceptId, payload);
      toast.success(method === "email" ? "Unlocked! Opening your full report…" : "Thanks! Your report is unlocked — we'll also message you on WhatsApp.");
      onOpenChange(false);
      onUnlocked?.({ token: res.token, method: res.method });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not unlock the report");
    } finally {
      setBusy(false);
    }
  };

  const tab = (key, Icon, label) => (
    <button type="button" onClick={() => setMethod(key)} data-testid={`gate-method-${key}`}
      className={`flex-1 flex items-center justify-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold transition-colors ${method === key ? "border-brand bg-brand/5 text-brand" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>
      <Icon size={15} /> {label}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="rebuild-gate-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2"><Wand2 size={20} className="text-brand" /> Your redesign is ready</DialogTitle>
          <DialogDescription>
            We've prepared the full transformation report for <span className="font-semibold text-slate-700">{domain}</span>. Where should we send it? Pick one — no password needed.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3 mt-1">
          <div className="flex gap-2">
            {tab("email", Mail, "Email")}
            {tab("whatsapp", MessageCircle, "WhatsApp")}
          </div>

          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name (optional)" data-testid="gate-name"
            className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30" />

          {method === "email" ? (
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" required autoFocus data-testid="gate-email"
              className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30" />
          ) : (
            <>
              <input type="tel" value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} placeholder="+1 555 123 4567 (with country code)" required autoFocus data-testid="gate-whatsapp"
                className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30" />
              <label className="flex items-start gap-2.5 text-xs text-slate-500 leading-relaxed cursor-pointer">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="gate-consent" className="mt-0.5 accent-[color:var(--brand,#2563EB)]" />
                <span>I agree to receive this requested website report and related follow-up messages from WebsiteTech.pro on WhatsApp. I can opt out anytime.</span>
              </label>
            </>
          )}

          <select value={goal} onChange={(e) => setGoal(e.target.value)} data-testid="gate-goal"
            className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-600 focus:outline-none focus:ring-2 focus:ring-brand/30 bg-white">
            {GOALS.map((g) => <option key={g.key} value={g.key}>{g.label === "Let AI decide" ? "Primary goal — Let AI decide" : g.label}</option>)}
          </select>

          <button type="submit" disabled={busy} data-testid="gate-submit" className="w-full btn-primary rounded-xl px-5 py-3 flex items-center justify-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 className="animate-spin" size={16} /> : <Download size={16} />} Send my free redesign report
          </button>
          <p className="text-[11px] text-slate-400 text-center">Free concept preview • No credit card • No website credentials</p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
